
import { useAuth, useLanguage, useNetwork } from '@/src/contexts';
import { TranslationKey, getNetworkConfig, getText, normalizeCurrencyCode } from '@/src/helpers';
import { extractAmountValue } from '@/src/helpers/amounts';
import { useTransactionHistory } from '@/src/hooks';
import { useFireblocksVaultAccount } from '@/src/hooks/apis/fireblocks';
import { Page } from '@/src/ui-components';
import { Button } from '@/src/ui-components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/src/ui-components/ui/popover';
import { ChevronsRight, Copy, ExternalLink, Info, Loader } from 'lucide-react';
import { toast } from 'sonner';

function HistoryContent() {
  const { language } = useLanguage();
  const { network } = useNetwork();
  const { user } = useAuth();

  // Helper function to format amount with decimal padding
  const formatAmount = (value: string | number | object): string => {
    const rawValue = extractAmountValue(value as any);
    if (rawValue === '-') return '-';

    const numValue = parseFloat(String(rawValue));
    if (isNaN(numValue)) return String(rawValue);

    // Format with 2 decimal places
    return numValue.toFixed(2);
  };

  // Helper function to extract OfferCreate execution details from AffectedNodes
  const getOfferExecutionDetails = (tx: any) => {
    if (tx.type !== 'OfferCreate' || !tx.meta?.AffectedNodes) {
      return null;
    }

    const userAccount = account?.toLowerCase();
    let executedBase: { amount: number; currency: string; issuer?: string } | null = null;
    let executedQuote: { amount: number; currency: string; issuer?: string } | null = null;

    // Parse AffectedNodes to find balance changes
    for (const node of tx.meta.AffectedNodes) {
      const modifiedNode = node.ModifiedNode || node.CreatedNode || node.DeletedNode;
      if (!modifiedNode) continue;

      // Check for AccountRoot changes (XRP balance changes)
      if (modifiedNode.LedgerEntryType === 'AccountRoot') {
        const finalFields = modifiedNode.FinalFields || modifiedNode.NewFields;
        const previousFields = modifiedNode.PreviousFields;

        if (finalFields?.Account?.toLowerCase() === userAccount && previousFields?.Balance) {
          const balanceChange = parseInt(finalFields.Balance) - parseInt(previousFields.Balance);

          // Skip fee-only changes (typically 10-20 drops)
          // Only consider XRP changes that are significant (> 1000 drops = 0.001 XRP)
          if (balanceChange !== 0 && Math.abs(balanceChange) > 1000) {
            const amount = Math.abs(balanceChange) / 1000000; // Convert drops to XRP

            if (balanceChange < 0) {
              executedQuote = { amount, currency: 'XRP' };
            } else {
              executedBase = { amount, currency: 'XRP' };
            }
          }
        }
      }

      // Check for RippleState changes (issued currency balance changes)
      if (modifiedNode.LedgerEntryType === 'RippleState') {
        const finalFields = modifiedNode.FinalFields || modifiedNode.NewFields;
        const previousFields = modifiedNode.PreviousFields;

        const isLowLimit = finalFields?.LowLimit?.issuer?.toLowerCase() === userAccount;
        const isHighLimit = finalFields?.HighLimit?.issuer?.toLowerCase() === userAccount;

        if (!isLowLimit && !isHighLimit) continue;

        // Handle both ModifiedNode (existing trust line) and CreatedNode (new trust line)
        if (node.CreatedNode) {
          // New trust line - the balance is the amount received/paid
          const balance = finalFields.Balance;
          const balanceValue = parseFloat(balance?.value || balance || '0');

          if (balanceValue !== 0) {
            const currency = balance?.currency || 'Unknown';
            const amount = Math.abs(balanceValue);

            // Get the actual issuer (the one who isn't the user)
            const issuerAccount = isLowLimit ? finalFields.HighLimit?.issuer : finalFields.LowLimit?.issuer;

            // For CreatedNode in OfferCreate transactions:
            // Balance represents the token holdings from low account's perspective
            // Positive balance = low account holds tokens (received from high)
            // Negative balance = low account owes tokens (sent to high)

            if (isLowLimit) {
              // User is on low side
              if (balanceValue > 0) {
                // Positive = user received/holds tokens
                executedBase = { amount, currency: normalizeCurrencyCode(currency) || currency, issuer: issuerAccount };
              } else {
                // Negative = user sent/owes tokens
                executedQuote = { amount, currency: normalizeCurrencyCode(currency) || currency, issuer: issuerAccount };
              }
            } else {
              // User is on high side (issuer perspective is inverted)
              if (balanceValue > 0) {
                // Positive from low side = high side sent tokens
                executedQuote = { amount, currency: normalizeCurrencyCode(currency) || currency, issuer: issuerAccount };
              } else {
                // Negative from low side = high side received tokens
                executedBase = { amount, currency: normalizeCurrencyCode(currency) || currency, issuer: issuerAccount };
              }
            }
          }
        } else if (previousFields?.Balance) {
          // Modified trust line - calculate the difference
          const prevBalance = parseFloat(previousFields.Balance.value || previousFields.Balance);
          const finalBalance = parseFloat(finalFields.Balance.value || finalFields.Balance);

          // For RippleState, perspective matters
          const balanceChange = isLowLimit
            ? finalBalance - prevBalance  // Low limit perspective
            : prevBalance - finalBalance; // High limit perspective (inverted)

          if (balanceChange !== 0) {
            const currency = finalFields.Balance.currency || 'Unknown';
            const issuer = finalFields.HighLimit?.issuer || finalFields.LowLimit?.issuer;
            const amount = Math.abs(balanceChange);

            if (balanceChange < 0) {
              executedQuote = { amount, currency: normalizeCurrencyCode(currency) || currency, issuer };
            } else {
              executedBase = { amount, currency: normalizeCurrencyCode(currency) || currency, issuer };
            }
          }
        }
      }
    }

    return { executedBase, executedQuote };
  };

  // Helper function to get transaction icon
  const getTransactionIcon = (tx: any) => {
    if (tx.type === 'Payment' && tx.destination) {
      const userAccount = account?.toLowerCase();
      const txAccount = tx.account?.toLowerCase();
      const txDestination = tx.destination?.toLowerCase();

      if (txAccount === userAccount) {
        // User sent the payment - red outgoing
        return '/tx-payment-outgoing.svg';
      } else if (txDestination === userAccount) {
        // User received the payment - green incoming
        return '/tx-payment-incoming.svg';
      }
    }

    if (tx.type === 'TrustSet') {
      return '/tx-trustset.svg';
    }

    if (tx.type === 'OfferCreate') {
      return '/tx-offercreate.svg';
    }

    if (tx.type === 'OfferCancel') {
      return '/tx-offercancel.svg';
    }

    return null;
  };

  // Helper function to get From/To information
  const getFromToInfo = (tx: any) => {
    if (tx.type === 'Payment' && tx.destination) {
      const userAccount = account?.toLowerCase();
      const txAccount = tx.account?.toLowerCase();
      const txDestination = tx.destination?.toLowerCase();

      if (txAccount === userAccount) {
        // User sent the payment
        return { label: 'To', address: tx.destination };
      } else if (txDestination === userAccount) {
        // User received the payment
        return { label: 'From', address: tx.account };
      }
    }

    // Handle TrustSet - show the issuer as counterparty
    if (tx.type === 'TrustSet' && tx.limit_amount?.issuer) {
      return { label: 'Issuer', address: tx.limit_amount.issuer };
    }

    // Handle OfferCreate - check if there was an execution
    if (tx.type === 'OfferCreate') {
      // Check if there was execution by looking at AffectedNodes
      if (tx.meta?.AffectedNodes) {
        const userAccount = account?.toLowerCase();
        const txAccount = tx.account?.toLowerCase();
        const isTaker = txAccount === userAccount;
        let counterparty = null;

        // If user is the maker (didn't submit this tx), then the taker is tx.Account
        if (!isTaker) {
          return { label: 'Taker', address: tx.account };
        }

        // If user is the taker, find the maker from the modified/deleted Offer node
        for (const node of tx.meta.AffectedNodes) {
          const modifiedNode = node.ModifiedNode || node.DeletedNode;
          if (!modifiedNode) continue;

          if (modifiedNode.LedgerEntryType === 'Offer') {
            const finalFields = modifiedNode.FinalFields || modifiedNode.NewFields;
            const offerAccount = finalFields?.Account;

            if (offerAccount && offerAccount.toLowerCase() !== userAccount) {
              // Found the maker's offer
              counterparty = { label: 'Maker', address: offerAccount };
              return counterparty;
            }
          }
        }

        // Check for AMM (if user is taker but no regular offer was found)
        for (const node of tx.meta.AffectedNodes) {
          const modifiedNode = node.ModifiedNode || node.CreatedNode || node.DeletedNode;
          if (!modifiedNode) continue;

          if (modifiedNode.LedgerEntryType === 'AccountRoot') {
            const finalFields = modifiedNode.FinalFields || modifiedNode.NewFields;
            const ammAccount = finalFields?.Account;

            if (finalFields?.AMMID && ammAccount?.toLowerCase() !== userAccount) {
              return { label: 'AMM', address: ammAccount };
            }
          }
        }

        return counterparty;
      }

      return null;
    }

    return null;
  };

  // Helper function to extract asset info from transaction
  const getAssetInfo = (tx: any) => {
    // Handle Payment transactions
    if (tx.type === 'Payment' && tx.delivered_amount) {
      // Check if delivered_amount is an object (issued currency)
      if (typeof tx.delivered_amount === 'object' && tx.delivered_amount.currency) {
        return {
          assetName: normalizeCurrencyCode(tx.delivered_amount.currency) || tx.delivered_amount.currency,
          issuer: tx.delivered_amount.issuer,
        };
      } else {
        // XRP native currency
        return {
          assetName: 'XRP',
          issuer: null,
        };
      }
    }

    // Handle TrustSet transactions
    if (tx.type === 'TrustSet' && tx.limit_amount) {
      return {
        assetName: normalizeCurrencyCode(tx.limit_amount.currency) || tx.limit_amount.currency,
        issuer: tx.limit_amount.issuer,
      };
    }

    // Handle OfferCreate transactions - show the base asset (TakerGets)
    if (tx.type === 'OfferCreate' && tx.taker_gets) {
      if (typeof tx.taker_gets === 'object' && tx.taker_gets.currency) {
        return {
          assetName: normalizeCurrencyCode(tx.taker_gets.currency) || tx.taker_gets.currency,
          issuer: tx.taker_gets.issuer,
        };
      } else {
        return {
          assetName: 'XRP',
          issuer: null,
        };
      }
    }

    return { assetName: '-', issuer: null };
  };

  // Use Fireblocks authentication instead of wallet connection
  const { data: vaultAccount } = useFireblocksVaultAccount();

  // Get XRPL address directly from auth context (no API calls needed)
  const xrplAddress = user?.xrp_address;
  const account = xrplAddress;

  const getTranslation = (key: TranslationKey) => getText(language, key);

  // Use the new hook for transaction history
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error, isError } =
    useTransactionHistory({
      account: account || '',
      network,
      pageSize: 20,
    });

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    }
  };

  // Flatten all pages into a single array of transactions
  const allTransactions = data?.pages.flatMap((page) => page.transactions) || [];

  // Calculate how many pages we've loaded
  const loadedPages = data?.pages.length || 0;

  // Show component if we have Fireblocks authentication and XRPL address
  if (!vaultAccount?.id || !xrplAddress || !network.startsWith('xrpl')) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-muted-foreground">
          Please configure your Fireblocks account to view transaction history
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-red-500">Error loading transaction history: {error?.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="rounded-lg border p-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-medium">{getTranslation('walletAddress')}:</h2>
            <a
              href={`${getNetworkConfig(network).explorerUrl}/accounts/${account}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-sm text-primary hover:underline"
            >
              {account}
            </a>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                if (account) {
                  navigator.clipboard.writeText(account);
                  toast.success('Address copied to clipboard');
                }
              }}
            >
              <Copy size={16} strokeWidth={1.5} absoluteStrokeWidth={true} />
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border">
        {isLoading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="h-4 w-32 rounded bg-muted"></div>
                  <div className="flex-1">
                    <div className="h-4 w-24 rounded bg-muted"></div>
                  </div>
                  <div className="h-4 w-20 rounded bg-muted"></div>
                </div>
              ))}
            </div>
          </div>
        ) : allTransactions.length === 0 ? (
          <div className="flex h-32 items-center justify-center">
            <p className="text-muted-foreground">No history found.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="p-2 text-left text-sm">Transaction</th>
                    <th className="p-2 text-left text-sm">Counterparty</th>
                    <th className="p-2 text-right text-sm">Details</th>
                    <th className="p-2 text-right text-sm">Fee</th>
                    <th className="p-2 text-right text-sm">Date/Time (UTC)</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {allTransactions.map((tx) => {
                    const assetInfo = getAssetInfo(tx);
                    const fromToInfo = getFromToInfo(tx);
                    const txIcon = getTransactionIcon(tx);
                    const offerExecution = getOfferExecutionDetails(tx);
                    return (
                      <tr key={tx.hash} className="border-b">
                        <td className="p-2">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              {txIcon && (
                                <img
                                  src={txIcon}
                                  alt={tx.type}
                                  width={32}
                                  height={32}
                                  className="flex-shrink-0"
                                />
                              )}
                              {/* Success/Failure Icon */}
                              {tx.result === 'tesSUCCESS' ? (
                                <img src="/icon-success.svg" alt="Success" width={32} height={32} className="flex-shrink-0" />
                              ) : (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <button className="inline-flex items-center flex-shrink-0">
                                      <img src="/icon-failure.svg" alt="Failed" width={32} height={32} />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto max-w-md">
                                    <div className="space-y-2">
                                      <h4 className="font-medium text-sm">Transaction Failed</h4>
                                      <p className="font-mono text-xs text-red-600 dark:text-red-400">{tx.result}</p>
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              )}
                            </div>
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-medium font-mono ${
                                tx.type === 'Payment'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                  : tx.type === 'OfferCreate'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                                    : tx.type === 'OfferCancel'
                                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                              }`}
                            >
                              {tx.type}
                            </span>
                          </div>
                        </td>
                        <td className="p-2">
                          {fromToInfo ? (
                            <div className="flex flex-col">
                              <span className="text-xs font-medium">{fromToInfo.label}:</span>
                              <span className="font-mono text-xs text-muted-foreground">
                                {fromToInfo.address}
                              </span>
                            </div>
                          ) : (
                            <span className="font-mono text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-2 text-right text-sm font-mono">
                          {tx.type === 'Payment' && tx.delivered_amount
                            ? (
                              <div className="flex items-center justify-end gap-1">
                                <span>{formatAmount(tx.delivered_amount)}</span>
                                <span className="text-xs text-muted-foreground">{assetInfo.assetName}</span>
                                {assetInfo.issuer && (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button className="inline-flex items-center">
                                        <Info size={14} className="text-muted-foreground hover:text-foreground" />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto max-w-md">
                                      <div className="space-y-2">
                                        <h4 className="font-medium text-sm">Issuer</h4>
                                        <p className="font-mono text-xs break-all">{assetInfo.issuer}</p>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                )}
                              </div>
                            )
                            : tx.type === 'TrustSet' && tx.limit_amount
                              ? (
                                <div className="flex items-center justify-end gap-1">
                                  <span>{formatAmount(tx.limit_amount)}</span>
                                  <span className="text-xs text-muted-foreground">{assetInfo.assetName}</span>
                                  {assetInfo.issuer && (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button className="inline-flex items-center">
                                          <Info size={14} className="text-muted-foreground hover:text-foreground" />
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto max-w-md">
                                        <div className="space-y-2">
                                          <h4 className="font-medium text-sm">Issuer</h4>
                                          <p className="font-mono text-xs break-all">{assetInfo.issuer}</p>
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                </div>
                              )
                              : tx.type === 'OfferCreate' && tx.taker_gets && tx.taker_pays
                                ? (
                                  <div className="flex items-start justify-between gap-4 w-full">
                                    {/* Offer Details - Left aligned */}
                                    <div className="space-y-0.5 flex flex-col items-start">
                                      <div className="text-xs text-muted-foreground font-semibold">Offer:</div>
                                      <div className="flex items-center gap-1">
                                        <img src="/icon-sell.svg" alt="Sell" width={12} height={12} />
                                        {formatAmount(tx.taker_gets)}
                                        <span className="text-xs text-muted-foreground">
                                          {typeof tx.taker_gets === 'object' && tx.taker_gets.currency
                                            ? normalizeCurrencyCode(tx.taker_gets.currency) || tx.taker_gets.currency
                                            : 'XRP'}
                                        </span>
                                        {typeof tx.taker_gets === 'object' && tx.taker_gets.issuer && (
                                          <Popover>
                                            <PopoverTrigger asChild>
                                              <button className="inline-flex items-center">
                                                <Info size={14} className="text-muted-foreground hover:text-foreground" />
                                              </button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto max-w-md">
                                              <div className="space-y-2">
                                                <h4 className="font-medium text-sm">Issuer</h4>
                                                <p className="font-mono text-xs break-all">{tx.taker_gets.issuer}</p>
                                              </div>
                                            </PopoverContent>
                                          </Popover>
                                        )}
                                        <img src="/icon-exchange.svg" alt="for" width={12} height={12} className="text-muted-foreground" />
                                        <img src="/icon-buy.svg" alt="Buy" width={12} height={12} />
                                        {formatAmount(tx.taker_pays)}
                                        <span className="text-xs text-muted-foreground">
                                          {typeof tx.taker_pays === 'object' && tx.taker_pays.currency
                                            ? normalizeCurrencyCode(tx.taker_pays.currency) || tx.taker_pays.currency
                                            : 'XRP'}
                                        </span>
                                        {typeof tx.taker_pays === 'object' && tx.taker_pays.issuer && (
                                          <Popover>
                                            <PopoverTrigger asChild>
                                              <button className="inline-flex items-center">
                                                <Info size={14} className="text-muted-foreground hover:text-foreground" />
                                              </button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto max-w-md">
                                              <div className="space-y-2">
                                                <h4 className="font-medium text-sm">Issuer</h4>
                                                <p className="font-mono text-xs break-all">{tx.taker_pays.issuer}</p>
                                              </div>
                                            </PopoverContent>
                                          </Popover>
                                        )}
                                      </div>
                                    </div>

                                    {/* Execution Details (if trade executed) - Right aligned */}
                                    {offerExecution && (offerExecution.executedBase || offerExecution.executedQuote) && (
                                      <div className="space-y-0.5 flex flex-col items-end">
                                        <div className="text-xs text-muted-foreground font-semibold">Executed:</div>
                                        <div className="flex items-center gap-1">
                                          {offerExecution.executedQuote && (
                                            <>
                                              <img src="/icon-paid.svg" alt="Paid" width={12} height={12} />
                                              {offerExecution.executedQuote.amount.toFixed(2)}
                                              <span className="text-xs text-muted-foreground">
                                                {offerExecution.executedQuote.currency}
                                              </span>
                                              {offerExecution.executedQuote.issuer && (
                                                <Popover>
                                                  <PopoverTrigger asChild>
                                                    <button className="inline-flex items-center">
                                                      <Info size={14} className="text-muted-foreground hover:text-foreground" />
                                                    </button>
                                                  </PopoverTrigger>
                                                  <PopoverContent className="w-auto max-w-md">
                                                    <div className="space-y-2">
                                                      <h4 className="font-medium text-sm">Issuer</h4>
                                                      <p className="font-mono text-xs break-all">{offerExecution.executedQuote.issuer}</p>
                                                    </div>
                                                  </PopoverContent>
                                                </Popover>
                                              )}
                                            </>
                                          )}
                                          {offerExecution.executedBase && offerExecution.executedQuote && (
                                            <img src="/icon-exchange.svg" alt="for" width={12} height={12} className="text-muted-foreground" />
                                          )}
                                          {offerExecution.executedBase && (
                                            <>
                                              <img src="/icon-got.svg" alt="Got" width={12} height={12} />
                                              {offerExecution.executedBase.amount.toFixed(2)}
                                              <span className="text-xs text-muted-foreground">
                                                {offerExecution.executedBase.currency}
                                              </span>
                                              {offerExecution.executedBase.issuer && (
                                                <Popover>
                                                  <PopoverTrigger asChild>
                                                    <button className="inline-flex items-center">
                                                      <Info size={14} className="text-muted-foreground hover:text-foreground" />
                                                    </button>
                                                  </PopoverTrigger>
                                                  <PopoverContent className="w-auto max-w-md">
                                                    <div className="space-y-2">
                                                      <h4 className="font-medium text-sm">Issuer</h4>
                                                      <p className="font-mono text-xs break-all">{offerExecution.executedBase.issuer}</p>
                                                    </div>
                                                  </PopoverContent>
                                                </Popover>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )
                                : tx.type === 'OfferCancel' && tx.offer_sequence
                                  ? (
                                    <div className="flex flex-col items-end">
                                      <span className="text-xs text-muted-foreground">Offer:</span>
                                      <span className="font-mono">#{tx.offer_sequence}</span>
                                    </div>
                                  )
                                  : '-'}
                        </td>
                        <td className="p-2 text-right text-sm font-mono">
                          <div className="flex items-center justify-end gap-1">
                            <span>{(parseInt(tx.fee) / 1000000).toFixed(5)}</span>
                            <span className="text-xs text-muted-foreground">XRP</span>
                          </div>
                        </td>
                        <td className="p-2 text-right text-sm font-mono text-muted-foreground">
                          {getRelativeTime(tx.date)}
                        </td>
                        <td className="p-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              window.open(
                                `${getNetworkConfig(network).explorerUrl}/transactions/${tx.hash}`,
                                '_blank'
                              )
                            }
                          >
                            <ExternalLink size={16} strokeWidth={1.5} absoluteStrokeWidth={true} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Load More */}
            <div className="flex flex-col items-center gap-3 border-t p-4">
              <div className="text-center text-sm text-muted-foreground">
                Showing {allTransactions.length} transactions
                {loadedPages > 1 && (
                  <span className="ml-2 text-xs">(loaded {loadedPages} pages)</span>
                )}
                {hasNextPage && <span className="ml-2 text-xs">• more available</span>}
              </div>

              {hasNextPage && (
                <Button
                  variant="outline"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="flex items-center gap-2"
                >
                  {isFetchingNextPage ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      Loading more...
                    </>
                  ) : (
                    <>
                      Load More{' '}
                      <ChevronsRight size={16} strokeWidth={1.5} absoluteStrokeWidth={true} />
                    </>
                  )}
                </Button>
              )}

              {!hasNextPage && allTransactions.length > 0 && (
                <div className="text-sm text-muted-foreground">All transactions loaded</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function HistoryPage() {
  return (
    <Page>
      <Page.Body className="bg-card">
        <HistoryContent />
      </Page.Body>
    </Page>
  );
}
